<template>
  <div class="date-time-picker-root">
    <div class="date-time-picker-row">
      <div class="form-group-header-label">
        FROM
      </div>
      <div :class="['date-time-picker-row__form-group', {'has-error':selectedFromTimeError}]">
        <div class="date-time-picker-row__form-label">
          Date:
        </div>
        <DatePicker
          class="date-time-picker-row__form-control"
          :model-value="selectedFromDate"
          @update:model-value="setSelectedFromDate"
          hide-placeholder
        />
        <div class="date-time-picker-row__form-label">
          Time:
        </div>
        <TimeInput
          :model-value="selectedFromTime"
          @update:model-value="setSelectedFromTime"
          :error="selectedFromTimeError"
          :disabled="!selectedFromDate"
        />
      </div>
      <div
        class="date-time-picker-row__form"
        v-if="selectedFromTimeError"
      >
        <div
          v-if="selectedFromTimeError"
          class="date-time-picker-row__form-error"
        >
          <Icon
            v-if="selectedFromTimeError !== ' '"
            class="date-time-picker-row__form-error-icon"
            name="exclamation"
          />
          <span class="date-time-picker-row__form-error-message">
            {{ selectedFromTimeError }}
          </span>
        </div>
      </div>
    </div>
    <div class="date-time-picker-row">
      <div class="form-group-header-label">
        UNTIL
      </div>
      <div :class="['date-time-picker-row__form-group', {'has-error':selectedUntilTimeError}]">
        <div class="date-time-picker-row__form-label">
          Date:
        </div>
        <DatePicker
          class="date-time-picker-row__form-control"
          :model-value="selectedUntilDate"
          @update:model-value="setSelectedUntilDate"
          hide-placeholder
        />
        <div class="date-time-picker-row__form-label">
          Time:
        </div>
        <TimeInput
          :model-value="selectedUntilTime"
          @update:model-value="setSelectedUntilTime"
          :error="selectedUntilTimeError"
          :disabled="!selectedUntilDate"
        />
      </div>
      <div
        class="date-time-picker-row__form"
        v-if="selectedUntilTimeError"
      >
        <div
          v-if="selectedUntilTimeError"
          class="date-time-picker-row__form-error"
        >
          <Icon
            v-if="selectedUntilTimeError !== ' '"
            class="date-time-picker-row__form-error-icon"
            name="exclamation"
          />
          <span class="date-time-picker-row__form-error-message">
            {{ selectedUntilTimeError }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { split } from 'lodash';
import {
  parseISO, isBefore,
} from 'date-fns';
import { convertTo24HourTime } from '@/services/helpers/time';
import DatePicker from '@/components/common/DatePicker';
import TimeInput from '@/components/common/TimeInput';
import Icon from '@/components/common/Icon';

export default {
  emits: ['onDateTimeUpdate'],
  components: {
    DatePicker,
    TimeInput,
    Icon,
  },
  setup(_, { emit }) {
    const selectedFromTime = ref('');
    const selectedFromDate = ref('');
    const selectedUntilTime = ref('');
    const selectedUntilDate = ref('');

    const selectedFromTimeError = computed(() => {
      if (selectedFromDate.value && !selectedFromTime.value) return 'Time should be selected';
      const [selectedTime] = split(selectedFromTime.value, ' ');
      if (selectedTime === '00:00') return 'Invalid value. Did you mean 12:00?';
      if (!selectedTime) return '';
      const [hours, minutes] = split(selectedTime, ':').map(Number);
      const isValidTime = selectedFromTime.value?.length === 8 && hours > 0 && hours <= 12 && minutes >= 0 && minutes <= 59;
      if (!isValidTime) return 'Invalid value';
      return '';
    });

    const selectedUntilTimeError = computed(() => {
      if (selectedUntilDate.value && !selectedUntilTime.value) return 'Time should be selected';
      const [selectedTime] = split(selectedUntilTime.value, ' ');
      if (selectedTime === '00:00') return 'Invalid value. Did you mean 12:00?';
      if (!selectedTime) return '';
      const [hours, minutes] = split(selectedTime, ':').map(Number);
      const isValidTime = selectedUntilTime.value?.length === 8 && hours > 0 && hours <= 12 && minutes >= 0 && minutes <= 59;
      if (!isValidTime) return 'Invalid value';
      if (selectedFromDate.value) {
        const dateTimeFrom = parseISO(`${selectedFromDate.value}T${convertTo24HourTime(selectedFromTime.value)}:00`);
        const dateTimUntil = parseISO(`${selectedUntilDate.value}T${convertTo24HourTime(selectedUntilTime.value)}:00`);
        if (isBefore(dateTimUntil, dateTimeFrom)) return 'Date until cannot be before the date from';
      }
      return '';
    });

    const onDateTimeUpdate = () => {
      emit('onDateTimeUpdate', {
        from: `${selectedFromDate.value}T${convertTo24HourTime(selectedFromTime.value)}:00`,
        until: `${selectedUntilDate.value}T${convertTo24HourTime(selectedUntilTime.value)}:00`,
        fieldMissing: !selectedFromTime.value || !selectedFromDate.value || !selectedUntilTime.value || !selectedUntilDate.value,
        timeError: !!selectedFromTimeError.value || !!selectedUntilTimeError.value,
      });
    };

    const setSelectedFromTime = (newSelectedTime) => {
      selectedFromTime.value = newSelectedTime;
      onDateTimeUpdate();
    };

    const setSelectedFromDate = (newSelectedDate) => {
      selectedFromDate.value = newSelectedDate || '';
      onDateTimeUpdate();
    };

    const setSelectedUntilTime = (newSelectedTime) => {
      selectedUntilTime.value = newSelectedTime;
      onDateTimeUpdate();
    };

    const setSelectedUntilDate = (newSelectedDate) => {
      selectedUntilDate.value = newSelectedDate || '';
      onDateTimeUpdate();
    };

    return {
      selectedFromTime,
      setSelectedFromTime,
      selectedFromDate,
      setSelectedFromDate,
      selectedFromTimeError,
      selectedUntilTime,
      setSelectedUntilTime,
      selectedUntilDate,
      setSelectedUntilDate,
      selectedUntilTimeError,
    };
  },
};
</script>

<style lang="scss">
  .date-time-picker-root {
    min-height: 100px;
    border: 1px solid #F0F0F0;
    border-radius: 4px;
    padding: 8px 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: relative;

    .date-time-picker-row {
      &__form {
        &-group {
          display: flex;
          gap: 6px;
          align-items: center;
          margin-bottom: 16px;

          &.has-error {
            margin-bottom: 4px;
          }

          &:last-child {
            margin-bottom: 0;
          }

          .date-picker {
            height: 32px;
            max-height: 32px;
            min-height: 32px;
          }

          .time-input {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            height: 32px;

            .select-input {
              width: 72px;
            }

            &__control {
              display: flex;
              align-items: center;
              background-color: $white;
              border: $textInputBorder;
              border-radius: $textInputBorderRadius;
              width: 96px;
              height: 100%;
              transition: $textInputBoxShadowTransition;
              padding: 0;

              &:not(&--is-invalid):focus-within {
                outline: 1px solid #a2bfff;
              }

              &--is-invalid {
                outline: 1px solid $error500;
              }
            }

            &__input {
              width: 100%;
              height: 100%;
              padding: 0 8px;

              &:focus {
                border: none;
                outline: none;
              }

              &::placeholder {
                color: #CDCDCD;
              }
            }
          }
        }
        &-error {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 10px;
          color: $error500;
          margin-top: 4px;
          margin-bottom: 8px;

          &-icon {
            flex-shrink: 0;
            width: 12px;
            height: 12px;
            border-radius: 100%;
            overflow: hidden;

            svg {
              background-color: $error500;
              fill: #fff;
              width: 100%;
              height: 100%;
            }
          }

          &-message {
            display: block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
      }
    }

    .form-group-header-label {
      font-size: 10px;
      color: #6D6D6D;
      font-weight: 600;
      font-family: 'Rubik', sans-serif;
      margin-bottom: 4px;
    }
  }
</style>
